import {
    CalendarOutlined,
    DownloadOutlined,
    FilePdfOutlined,
    PrinterOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    Col,
    ConfigProvider,
    DatePicker,
    Input,
    Modal,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
} from "antd";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import { COLORS } from "../../../components/ui/colors";
import axiosInstance from "../../../variables/api/axiosInstance";
import { frenchDate, frenchDateSimple } from "../../../variables/functions";
import EtatContratsImprimes from "./docs/EtatContratsImprimes";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Période par défaut = mois courant (listing léger) ; l'utilisateur élargit pour un état annuel.
const DEFAULT_RANGE = [dayjs().startOf("month"), dayjs().endOf("month")];

// Mappe une ligne backend (ContratSearchDTO) → ligne d'affichage / d'état.
const mapRow = (c, key) => ({
    key: c.codeId || key,
    codeId: c.codeId,
    numeroPolice: c.numeroPoliceP || c.numeroPoliceM,
    souscripteur: c?.prenom ? `${c?.prenom} ${c?.nom}` : `${c?.nom || ""}`,
    immatriculation: c?.immatriculation,
    organisme: c?.raisonSociale,
    dateEffet: c?.dateEffet ? frenchDate(c.dateEffet) : "",
    dateEcheance: c?.dateEcheance ? frenchDateSimple(c.dateEcheance) : "",
    regle: c?.reglements ? "REGLE" : "NON REGLE",
});

const PrintedContractsV2 = () => {
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState(DEFAULT_RANGE);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [exporting, setExporting] = useState(false);

    // État PDF
    const [etatData, setEtatData] = useState([]);
    const [showEtat, setShowEtat] = useState(false);
    const [buildingEtat, setBuildingEtat] = useState(false);
    const etatRef = useRef();
    const printEtat = useReactToPrint({ content: () => etatRef.current });

    const navigate = useNavigate();

    const params = useMemo(() => ({
        search: debouncedSearch || "",
        startDate: range?.[0] ? range[0].format("YYYY-MM-DD") : null,
        endDate: range?.[1] ? range[1].format("YYYY-MM-DD") : null,
    }), [debouncedSearch, range]);

    const periodeLabel = (range?.[0] && range?.[1])
        ? `PÉRIODE DU ${range[0].format("DD/MM/YYYY")} AU ${range[1].format("DD/MM/YYYY")}`
        : "";

    // Debounce de la recherche.
    useEffect(() => {
        const id = setTimeout(() => { setDebouncedSearch(searchText.trim()); setCurrentPage(1); }, 400);
        return () => clearTimeout(id);
    }, [searchText]);

    useEffect(() => {
        let active = true;
        setLoading(true);
        axiosInstance
            .get("/api/contrat/printed", { params: { ...params, page: currentPage - 1, size: pageSize } })
            .then((res) => {
                if (!active) return;
                const { content = [], totalElements = 0 } = res.data || {};
                setData(content.map(mapRow));
                setTotal(totalElements);
            })
            .catch(() => active && toast.error("Erreur lors du chargement des contrats imprimés"))
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [params, currentPage, pageSize]);

    // Récupère TOUTES les lignes de la période (pour l'état / l'export), au-delà de la page courante.
    const fetchAllRows = async () => {
        const size = Math.max(total, 1);
        const res = await axiosInstance.get("/api/contrat/printed", { params: { ...params, page: 0, size } });
        const { content = [] } = res.data || {};
        return content.map(mapRow);
    };

    const handleExcel = async () => {
        if (!range?.[0] || !range?.[1]) { toast.warning("Sélectionnez une période."); return; }
        setExporting(true);
        try {
            const rows = await fetchAllRows();
            const aoa = [
                ["N°", "N° Police", "Souscripteur", "Immatriculation", "Compagnie", "Date effet", "Date échéance", "Statut"],
                ...rows.map((c, i) => [
                    i + 1, c.numeroPolice || "", c.souscripteur || "", c.immatriculation || "",
                    c.organisme || "", c.dateEffet || "", c.dateEcheance || "",
                    c.regle === "REGLE" ? "Réglé" : "Non réglé",
                ]),
            ];
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Contrats imprimés");
            XLSX.writeFile(wb, `contrats_imprimes_${dayjs().format("YYYY-MM-DD")}.xlsx`);
        } catch (e) {
            toast.error("Erreur lors de l'export Excel.");
        } finally {
            setExporting(false);
        }
    };

    const handleEtatPdf = async () => {
        if (!range?.[0] || !range?.[1]) { toast.warning("Sélectionnez une période."); return; }
        setBuildingEtat(true);
        try {
            const rows = await fetchAllRows();
            setEtatData(rows);
            setShowEtat(true);
        } catch (e) {
            toast.error("Erreur lors de la génération de l'état.");
        } finally {
            setBuildingEtat(false);
        }
    };

    const columns = [
        { title: "N° Police", dataIndex: "numeroPolice", key: "numeroPolice", render: (v) => <Text strong>{v}</Text> },
        { title: "Souscripteur", dataIndex: "souscripteur", key: "souscripteur" },
        { title: "Immatriculation", dataIndex: "immatriculation", key: "immatriculation" },
        { title: "Compagnie", dataIndex: "organisme", key: "organisme" },
        { title: "Date effet", dataIndex: "dateEffet", key: "dateEffet" },
        { title: "Date échéance", dataIndex: "dateEcheance", key: "dateEcheance" },
        {
            title: "Statut", dataIndex: "regle", key: "regle",
            render: (v) => <Tag color={v === "REGLE" ? "success" : "warning"} style={{ borderRadius: 10 }}>
                {v === "REGLE" ? "RÉGLÉ" : "NON RÉGLÉ"}
            </Tag>,
        },
        {
            title: "", key: "actions", width: 60,
            render: (_, r) => (
                <Button size="small" icon={<SearchOutlined />} onClick={() => navigate(`/details-contrat/${r.codeId}`)} />
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"PRODUCTION"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{
                                background: COLORS.gradient, borderRadius: 16, padding: "24px 28px",
                                marginBottom: 20, color: "#fff", boxShadow: "0 10px 24px rgba(36,33,102,0.25)",
                                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16,
                            }}>
                                <div>
                                    <Title level={2} style={{ color: "#fff", margin: 0 }}>Contrats imprimés</Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        États sur une période
                                        <Tag style={{ marginLeft: 8, borderRadius: 10 }} color="gold">Aperçu v2</Tag>
                                    </Text>
                                </div>
                                <Space wrap>
                                    <Button size="large" icon={<DownloadOutlined />} loading={exporting} onClick={handleExcel}
                                        style={{ borderRadius: 10, height: 44, fontWeight: 600 }}>
                                        Exporter Excel
                                    </Button>
                                    <Button size="large" type="primary" icon={<FilePdfOutlined />} loading={buildingEtat} onClick={handleEtatPdf}
                                        style={{ borderRadius: 10, height: 44, fontWeight: 600 }}>
                                        État PDF
                                    </Button>
                                </Space>
                            </div>

                            {/* Filtres */}
                            <Card bordered={false} style={{ borderRadius: 16, marginBottom: 20, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 16 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={10}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Période</Text>
                                        <ConfigProvider locale={frFR}>
                                            <RangePicker
                                                value={range}
                                                onChange={(v) => { setRange(v); setCurrentPage(1); }}
                                                format="DD/MM/YYYY"
                                                allowClear={false}
                                                suffixIcon={<CalendarOutlined />}
                                                style={{ width: "100%" }}
                                            />
                                        </ConfigProvider>
                                    </Col>
                                    <Col xs={24} md={10}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Recherche</Text>
                                        <Input
                                            allowClear
                                            prefix={<SearchOutlined />}
                                            placeholder="N° police, souscripteur…"
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={24} md={4}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Total période</Text>
                                        <Text strong style={{ fontSize: 20, color: COLORS.secondary }}>{total}</Text>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Tableau */}
                            <Card bordered={false} style={{ borderRadius: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 8 }}>
                                <Spin spinning={loading}>
                                    <Table
                                        rowKey="key"
                                        columns={columns}
                                        dataSource={data}
                                        size="middle"
                                        pagination={{
                                            current: currentPage,
                                            pageSize,
                                            total,
                                            showSizeChanger: true,
                                            onChange: (p, s) => { setCurrentPage(p); setPageSize(s); },
                                        }}
                                    />
                                </Spin>
                            </Card>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>

            {/* Modale aperçu / impression de l'ÉTAT (format en-tête/pied habituel) */}
            <Modal
                title={<Space><FilePdfOutlined style={{ color: COLORS.primary }} />État des contrats imprimés</Space>}
                centered
                open={showEtat}
                onCancel={() => setShowEtat(false)}
                width={1250}
                zIndex={2000}
                footer={[
                    <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={printEtat}>Imprimer</Button>,
                    <Button key="close" onClick={() => setShowEtat(false)}>Fermer</Button>,
                ]}
            >
                <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <EtatContratsImprimes ref={etatRef} contrats={etatData} periode={periodeLabel} />
                </div>
            </Modal>
        </div>
    );
};

export default PrintedContractsV2;
